import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, InputAdornment, Paper, TextField, Typography } from '@mui/material';
import SearchIcon       from '@mui/icons-material/Search';
import Colors           from '../../../library/Colors';
import i18n             from '../../../i18n';
import {
    buildFullIndexFromBundle, buildSearchIndex, rankMatches,
    getKnownCardAnchors, MAIN_CONFIG_TAB_KEYS, SUB_CRUMB_KEY,
} from './searchCore';

// Re-exported: permissionCatalog.js derives the role catalog from these.
export { getKnownCardAnchors, MAIN_CONFIG_TAB_KEYS, SUB_CRUMB_KEY };

// ────────────────────────────────────────────────────────────────────────
// Webshop config search.
//
// Always-on search bar in the WebshopConfigScreen header. It jumps the user
// to any tab / sub-tab / config modal. (Previously gated behind a "click
// search bar — shown by default in the config header.)
//
// Usage in the parent screen:
//   const search = useWebshopSearch({ ...adapters });
//   <WebshopSearchBar search={search} />
//
// All admin labels MUST go through i18n (cfg_* keys) so the search index
// reads the same translations the tabs render — see the search-navigation
// project memory for context.
// ────────────────────────────────────────────────────────────────────────

// ────────────────────────────────────────────────────────────────────────
// Anchor pub-sub. When the user picks a search result that points at a
// specific card, we dispatch the card's anchor id here. ConfigCards that
// match the id scroll into view and briefly flash to draw attention.
//
// We also keep the most recent request as a "pending" anchor for a short
// TTL: when a search jumps to a modal, the matching ConfigCard isn't
// mounted yet at dispatch time, so it would miss the live event. By
// stashing the anchor for ~600ms, cards that mount just after the
// dispatch can still pick it up on first effect.
// ────────────────────────────────────────────────────────────────────────
const _anchorListeners = new Set();
const ANCHOR_PENDING_TTL_MS = 600;
let _pendingAnchor = null;
let _pendingAnchorAt = 0;
export const requestAnchor = (anchorId) => {
    if (!anchorId) return;
    _pendingAnchor = anchorId;
    _pendingAnchorAt = Date.now();
    _anchorListeners.forEach((fn) => fn(anchorId));
    // Auto-expire so a later, unrelated mount doesn't latch onto a stale anchor.
    setTimeout(() => {
        if (_pendingAnchor === anchorId && Date.now() - _pendingAnchorAt >= ANCHOR_PENDING_TTL_MS - 5) {
            _pendingAnchor = null;
        }
    }, ANCHOR_PENDING_TTL_MS);
};
export const useAnchor = (anchorId) => {
    const [flash, setFlash] = useState(false);
    useEffect(() => {
        if (!anchorId) return undefined;
        let timer = null;
        const trigger = () => {
            const node = document.getElementById(anchorId);
            if (node && typeof node.scrollIntoView === 'function') {
                node.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
            setFlash(true);
            if (timer) clearTimeout(timer);
            timer = setTimeout(() => setFlash(false), 1500);
        };
        const handler = (id) => { if (id === anchorId) trigger(); };
        _anchorListeners.add(handler);
        // If a request was just made (e.g. our card mounted via a modal that
        // opened in response to a search hit), claim it now.
        if (_pendingAnchor === anchorId && Date.now() - _pendingAnchorAt < ANCHOR_PENDING_TTL_MS) {
            _pendingAnchor = null;
            // Defer one tick so the DOM node is laid out before scrollIntoView.
            setTimeout(trigger, 0);
        }
        return () => {
            _anchorListeners.delete(handler);
            if (timer) clearTimeout(timer);
        };
    }, [anchorId]);
    return flash;
};

// ── Modal-internal tab pub-sub ──────────────────────────────────────────────
// Some modals/panels keep their active sub-tab in LOCAL state (carousel &
// image-grid Slides/Settings, Cadeaubon's settings/layouts/translations). A
// search hit can open the modal but couldn't pick the right inner tab. This
// pub-sub lets the search request a tab by a stable `key`; the host subscribes
// via useModalTab and sets its own state. Mirrors the anchor pub-sub, incl. a
// short "pending" window so a host that mounts JUST after the request (e.g. the
// carousel dialog that openBlockConfig just opened) still picks it up.
const MODAL_TAB_TTL_MS = 800;
let _pendingModalTab = null; // { key, tab, at }
const _modalTabListeners = new Set();
export const requestModalTab = (key, tab) => {
    if (!key || tab == null) return;
    _pendingModalTab = { key, tab, at: Date.now() };
    _modalTabListeners.forEach((fn) => fn(key, tab));
    setTimeout(() => {
        if (_pendingModalTab && Date.now() - _pendingModalTab.at >= MODAL_TAB_TTL_MS - 5) _pendingModalTab = null;
    }, MODAL_TAB_TTL_MS);
};
export const useModalTab = (key, onTab) => {
    useEffect(() => {
        if (!key || typeof onTab !== 'function') return undefined;
        // Claim a request that fired just before this host mounted.
        if (_pendingModalTab && _pendingModalTab.key === key && Date.now() - _pendingModalTab.at < MODAL_TAB_TTL_MS) {
            const { tab } = _pendingModalTab;
            _pendingModalTab = null;
            setTimeout(() => onTab(tab), 0);
        }
        const handler = (k, t) => { if (k === key) onTab(t); };
        _modalTabListeners.add(handler);
        return () => { _modalTabListeners.delete(handler); };
    }, [key, onTab]);
};

// F05: cached per language at module scope — the walk over the whole i18n
// bundle (~10^5 startsWith calls through classifyKey) used to re-run on every
// MOUNT of the config screen, synchronously in the mount path. Invalidated
// implicitly per language; translations don't change within a session.
const _fullIndexCache = new Map();
const buildFullIndex = (lang) => {
    const key = lang || i18n.language || 'en';
    if (_fullIndexCache.has(key)) return _fullIndexCache.get(key);
    const built = buildFullIndexUncached(key);
    _fullIndexCache.set(key, built);
    return built;
};
const buildFullIndexUncached = (lang) =>
    buildFullIndexFromBundle(i18n.getResourceBundle(lang || i18n.language || 'en', 'translation'));

/**
 * Hook holding all search state + behaviour. Adapters (setTabValue, setXxxTab,
 * openStyleModal, notify) come from the parent screen so the search stays
 * decoupled from React-Router and the screen's own local state.
 */
// Map a search entry onto its permission-catalog key so results can be
// filtered by can() while a role is active/simulated. Mirrors the key scheme
// permissionCatalog.js derives FROM this file (card anchor → card key, else
// the entry's main tab) — defined here, not imported from the catalog,
// because the catalog imports from this module (avoid the cycle).
const SEARCH_TAB_PERMISSION_KEYS = [
    'webshop.config.tab.pages', 'webshop.config.tab.style', 'webshop.config.tab.features',
    'webshop.config.tab.processing', 'webshop.config.tab.sync',
];
export const permissionKeyForEntry = (entry) => {
    if (entry?.cardAnchor) return 'webshop.config.card.' + String(entry.cardAnchor).replace(/^cfg-card-/, '');
    if (typeof entry?.main === 'number') return SEARCH_TAB_PERMISSION_KEYS[entry.main] || null;
    return null;
};

export const useWebshopSearch = ({
    t,
    can,
    setTabValue,
    setFeaturesTab, setPagesTab, setStyleTab, setProcTab, setSyncTab,
    openStyleModal, openBlockConfig,
} = {}) => ({
    // F01: the hook now carries ONLY the navigation adapters. All search
    // state (query/open/matches/indexes) moved into WebshopSearchBar itself,
    // so typing in the search box no longer re-renders the whole config
    // screen — just the bar and its popover.
    t,
    can,
    jumpToEntry: (entry) => {
        if (setTabValue) setTabValue(entry.main);
        if (entry.sub) {
            if ('featuresTab' in entry.sub && setFeaturesTab) setFeaturesTab(entry.sub.featuresTab);
            if ('pagesTab'   in entry.sub && setPagesTab)   setPagesTab(entry.sub.pagesTab);
            if ('styleTab'   in entry.sub && setStyleTab)   setStyleTab(entry.sub.styleTab);
            if ('procTab'    in entry.sub && setProcTab)    setProcTab(entry.sub.procTab);
            if ('syncTab'    in entry.sub && setSyncTab)    setSyncTab(entry.sub.syncTab);
        }
        // Tab switch flushes first, then the modal opens against the new tree.
        if (entry.modal && openStyleModal) {
            setTimeout(() => openStyleModal(entry.modal.section, entry.modal.mode), 0);
        }
        // Pages block configs (carousel / image grid) open via their own gear
        // dialog (setConfigBlock), not openStyleModal — fire after the tab switch.
        if (entry.block && openBlockConfig) {
            setTimeout(() => openBlockConfig(entry.block), 0);
        }
        // Select the right inner sub-tab of the opened modal/panel (carousel &
        // image-grid Slides/Settings, Cadeaubon sub-tabs). The host claims this
        // via useModalTab — pending window covers a modal that mounts just after.
        if (entry.modalTab) {
            setTimeout(() => requestModalTab(entry.modalTab.key, entry.modalTab.tab), 0);
        }
        // Card-level anchor: scroll the matching ConfigCard into view and
        // flash it. Wait two ticks — first for the tab switch render, second
        // so that any modal that just opened has its cards mounted.
        if (entry.cardAnchor) {
            setTimeout(() => requestAnchor(entry.cardAnchor), entry.modal ? 60 : 0);
        }
    },
});

/**
 * Visual search bar — TextField + dropdown of matches. Renders nothing
 * Search state lives locally (F01) so typing never re-renders the screen.
 *
 * Keyboard:
 *   ↓ / ↑    move highlight through the result list
 *   Enter    jump to the highlighted result (or the first one if none yet)
 *   Tab      same as Enter — keeps the eg. spotlight-feel; also stops the
 *            browser from tabbing past the (otherwise useless) input
 *   Esc      close the popover without navigating
 */
export const WebshopSearchBar = ({ search }) => {
    // Search state lives HERE (F01): keystrokes only re-render this bar.
    const [searchQuery, setSearchQuery] = useState('');
    const [searchOpen,  setSearchOpen]  = useState(false);
    const [highlight, setHighlight] = useState(0);
    const t = search?.t;

    // Track the active language so the full-text index rebuilds when the
    // user switches NL/EN/FR.
    const [lang, setLang] = useState(() => i18n.language || 'en');
    useEffect(() => {
        const handler = (l) => setLang(l);
        i18n.on('languageChanged', handler);
        return () => i18n.off('languageChanged', handler);
    }, []);

    // Curated index: the high-value navigation targets (tabs, sub-tabs,
    // modal openers). Always shown first in results.
    const curatedIndex = useMemo(() => buildSearchIndex(t), [t]);
    // Full-text index: every `cfg_*` translation, rebuilt on language change.
    const fullIndex = useMemo(() => buildFullIndex(lang), [lang]);

    const searchMatches = useMemo(() => {
        // Role gating: hide results the active (or simulated) role can't
        // reach — can()'s container semantics keep a tab entry visible when
        // any of its descendants is still allowed. No can() = show all.
        const can = search?.can;
        const allowed = (it) => {
            if (!can) return true;
            const key = permissionKeyForEntry(it);
            return !key || can(key);
        };
        return rankMatches({ query: searchQuery, curatedIndex, fullIndex, allowed, t });
    // search?.can in the deps: switching the (simulated) role must recompute
    // the visible results, not just future keystrokes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchQuery, curatedIndex, fullIndex, t, search?.can]);

    const jumpTo = (entry) => {
        if (search?.jumpToEntry) search.jumpToEntry(entry);
        setSearchQuery('');
        setSearchOpen(false);
    };

    // Refs to each result row so we can scroll the active one into view
    // when the highlight moves past the visible area of the popover.
    const rowRefs = useRef([]);

    // Reset the highlight to the top whenever the result list changes
    // (new query, new matches, popover re-opened).
    useEffect(() => { setHighlight(0); }, [searchQuery, searchMatches.length]);

    // Bring the highlighted row into view. `block: 'nearest'` only scrolls
    // when the row is actually outside the visible area, so the popover
    // doesn't jiggle for already-visible items.
    useEffect(() => {
        const node = rowRefs.current[highlight];
        if (node && typeof node.scrollIntoView === 'function') {
            node.scrollIntoView({ block: 'nearest' });
        }
    }, [highlight]);

    const tr = (k, fb) => (typeof t === 'function' ? t(k, fb) : fb);
    const lastIdx = Math.max(0, searchMatches.length - 1);

    const onKeyDown = (e) => {
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setSearchOpen(true);
            setHighlight((h) => Math.min(lastIdx, h + 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setSearchOpen(true);
            setHighlight((h) => Math.max(0, h - 1));
        } else if (e.key === 'Enter' || e.key === 'Tab') {
            if (searchMatches.length === 0) return;
            // Tab default would move focus away — block that so the user
            // stays in the search flow.
            e.preventDefault();
            jumpTo(searchMatches[Math.min(highlight, lastIdx)]);
        } else if (e.key === 'Escape') {
            e.preventDefault();
            setSearchOpen(false);
        }
    };

    return (
        <Box sx={{ position: 'relative', flexShrink: 0, py: 1 }}>
            <TextField
                size="small"
                placeholder={tr('cfg_search_placeholder', 'Search settings…')}
                value={searchQuery}
                onChange={(e) => { setSearchQuery(e.target.value); setSearchOpen(true); }}
                onKeyDown={onKeyDown}
                onFocus={() => setSearchOpen(true)}
                // Small delay so a click on a result lands BEFORE the popover
                // closes from the blur. 150ms is long enough for the click
                // event to register, short enough to feel instant.
                onBlur={() => setTimeout(() => setSearchOpen(false), 150)}
                InputProps={{
                    startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: Colors.sft_blue }} /></InputAdornment>,
                }}
                // Grows with the screen, and a bit more while focused on wide
                // screens (the tabs to the left absorb it; the header stays on
                // one line).
                sx={{
                    width: { xs: 200, md: 260, lg: 320, xl: 360 },
                    transition: 'width 160ms ease',
                    '&:focus-within': { width: { xs: 200, md: 260, lg: 420, xl: 460 } },
                }}
            />
            {searchOpen && searchMatches.length > 0 && (
                <Paper
                    elevation={4}
                    sx={{
                        position: 'absolute', top: '100%', right: 0, mt: 0.5,
                        width: 460, maxWidth: 'calc(100vw - 32px)', zIndex: 50,
                        maxHeight: 440, overflowY: 'auto',
                        borderRadius: 1.5,
                    }}
                >
                    {searchMatches.map((m, i) => {
                        const isActive = i === Math.min(highlight, lastIdx);
                        return (
                            <Box
                                key={m.id}
                                ref={(el) => { rowRefs.current[i] = el; }}
                                // mouseDown + preventDefault so the input's blur
                                // doesn't fire and close the popover before the
                                // click lands.
                                onMouseDown={(e) => { e.preventDefault(); jumpTo(m); }}
                                onMouseEnter={() => setHighlight(i)}
                                sx={{
                                    px: 1.5, py: 1,
                                    cursor: 'pointer',
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                    '&:last-child': { borderBottom: 'none' },
                                    backgroundColor: isActive ? 'rgba(25,118,210,0.10)' : 'transparent',
                                    borderLeft: '3px solid',
                                    borderLeftColor: isActive ? 'primary.main' : 'transparent',
                                }}
                            >
                                {/* long help sentences: two lines max */}
                                <Typography variant="body2" sx={{
                                    fontWeight: 600,
                                    display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                                }}>
                                    {m.label}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                                    {m.displayCrumb || m.crumb}
                                </Typography>
                            </Box>
                        );
                    })}
                </Paper>
            )}
            {searchOpen && searchQuery.trim() && searchMatches.length === 0 && (
                <Paper
                    elevation={4}
                    sx={{
                        position: 'absolute', top: '100%', right: 0, mt: 0.5,
                        width: 460, maxWidth: 'calc(100vw - 32px)', zIndex: 50,
                        borderRadius: 1.5,
                        px: 1.5, py: 1.25,
                    }}
                >
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        {typeof t === 'function'
                            ? t('cfg_search_no_matches', { q: searchQuery, defaultValue: 'No matches for "{{q}}"' })
                            : `No matches for "${searchQuery}"`}
                    </Typography>
                </Paper>
            )}
        </Box>
    );
};

WebshopSearchBar.propTypes = {
    search: PropTypes.shape({
        searchEnabled:  PropTypes.bool,
        searchQuery:    PropTypes.string,
        searchOpen:     PropTypes.bool,
        searchMatches:  PropTypes.array,
        setSearchQuery: PropTypes.func,
        setSearchOpen:  PropTypes.func,
        jumpTo:         PropTypes.func,
    }),
};
